import * as React from 'react';
import { Drawer as DrawerPrimitive } from 'vaul';

import { OverlayHostProvider } from '@/components/ui/overlay-host';
import { cn } from '@/lib/utils';

/**
 * Whether this subtree already sits inside an open drawer.
 *
 * React context flows through the portal `DrawerContent` renders into, not just
 * the DOM, so a drawer mounted anywhere in another drawer's tree sees `true`
 * here — which is what lets {@link Drawer} pick the right vaul root by itself.
 */
const DrawerNestingContext = React.createContext(false);

/**
 * A drawer opened from inside another drawer has to be vaul's `NestedRoot`, or
 * the two fight over the body scroll lock and over dismissal.
 *
 * The caller cannot make that choice: `NestedRoot` throws outright when it is
 * *not* nested, and a panel like the opportunity drawer opens both from a page
 * and from inside the contact drawer. So the decision is made here instead, and
 * every drawer in the app gets it for free. A top-level drawer reads the
 * context default and renders exactly what it rendered before.
 */
function Drawer({ children, ...props }: React.ComponentProps<typeof DrawerPrimitive.Root>) {
    const isNested = React.useContext(DrawerNestingContext);
    const Root = isNested ? DrawerPrimitive.NestedRoot : DrawerPrimitive.Root;
    return (
        <DrawerNestingContext.Provider value={true}>
            <Root data-slot="drawer" {...props}>
                {children}
            </Root>
        </DrawerNestingContext.Provider>
    );
}

function DrawerTrigger({ ...props }: React.ComponentProps<typeof DrawerPrimitive.Trigger>) {
    return <DrawerPrimitive.Trigger data-slot="drawer-trigger" {...props} />;
}

function DrawerPortal({ ...props }: React.ComponentProps<typeof DrawerPrimitive.Portal>) {
    return <DrawerPrimitive.Portal data-slot="drawer-portal" {...props} />;
}

function DrawerClose({ ...props }: React.ComponentProps<typeof DrawerPrimitive.Close>) {
    return <DrawerPrimitive.Close data-slot="drawer-close" {...props} />;
}

function DrawerOverlay({
    className,
    ...props
}: React.ComponentProps<typeof DrawerPrimitive.Overlay>) {
    return (
        <DrawerPrimitive.Overlay
            data-slot="drawer-overlay"
            className={cn(
                'fixed inset-0 z-50 bg-(--overlay-color) data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0',
                className,
            )}
            {...props}
        />
    );
}

function DrawerContent({
    className,
    children,
    ...props
}: React.ComponentProps<typeof DrawerPrimitive.Content>) {
    // State, not a ref: the popups below need a re-render once the node exists.
    const [overlayHost, setOverlayHost] = React.useState<HTMLElement | null>(null);
    return (
        <DrawerPortal data-slot="drawer-portal">
            <DrawerOverlay />
            <DrawerPrimitive.Content
                data-slot="drawer-content"
                className={cn(
                    // `outline-none`: vaul defaults `autoFocus={false}`, so it
                    // preventDefaults Radix's open-autofocus and the trapped
                    // FocusScope parks focus on this container (`tabindex=-1`)
                    // instead of a child. The base layer paints `outline-ring/50`
                    // on `*`, so whenever `:focus-visible` matched — a
                    // keyboard-opened drawer — the UA outline rendered as a
                    // primary-colored ring around the entire panel. `DialogContent`
                    // already carries the same guard.
                    'group/drawer-content bg-background fixed z-50 flex h-auto flex-col outline-none',
                    'data-[vaul-drawer-direction=top]:inset-x-0 data-[vaul-drawer-direction=top]:top-0 data-[vaul-drawer-direction=top]:mb-24 data-[vaul-drawer-direction=top]:max-h-[80vh] data-[vaul-drawer-direction=top]:rounded-b-lg data-[vaul-drawer-direction=top]:border-b',
                    'data-[vaul-drawer-direction=bottom]:inset-x-0 data-[vaul-drawer-direction=bottom]:bottom-0 data-[vaul-drawer-direction=bottom]:mt-24 data-[vaul-drawer-direction=bottom]:max-h-[80vh] data-[vaul-drawer-direction=bottom]:rounded-t-lg data-[vaul-drawer-direction=bottom]:border-t',
                    'data-[vaul-drawer-direction=right]:border-border data-[vaul-drawer-direction=right]:top-0 data-[vaul-drawer-direction=right]:right-0 data-[vaul-drawer-direction=right]:left-auto data-[vaul-drawer-direction=right]:h-full data-[vaul-drawer-direction=right]:w-full data-[vaul-drawer-direction=right]:max-w-full data-[vaul-drawer-direction=right]:rounded-l-lg data-[vaul-drawer-direction=right]:border-l md:data-[vaul-drawer-direction=right]:max-w-[600px] lg:data-[vaul-drawer-direction=right]:max-w-[700px]',
                    // The right side's mirror: a side panel opens at the reading
                    // end, which is the left under a right-to-left language.
                    'data-[vaul-drawer-direction=left]:border-border data-[vaul-drawer-direction=left]:top-0 data-[vaul-drawer-direction=left]:left-0 data-[vaul-drawer-direction=left]:right-auto data-[vaul-drawer-direction=left]:h-full data-[vaul-drawer-direction=left]:w-full data-[vaul-drawer-direction=left]:max-w-full data-[vaul-drawer-direction=left]:rounded-r-lg data-[vaul-drawer-direction=left]:border-r md:data-[vaul-drawer-direction=left]:max-w-[600px] lg:data-[vaul-drawer-direction=left]:max-w-[700px]',
                    className,
                )}
                {...props}
            >
                <div className="bg-muted mx-auto mt-4 hidden h-2 w-[100px] shrink-0 rounded-full group-data-[vaul-drawer-direction=bottom]/drawer-content:block" />
                <OverlayHostProvider host={overlayHost}>{children}</OverlayHostProvider>
                {/* Zero-size and out of flow, so hosting a popup never shifts the
                    drawer's own layout — the same host `SheetContent` keeps. A
                    popup portaled to <body> sits outside the drawer's focus trap,
                    so its search box could not keep focus. See `overlay-host.tsx`. */}
                <div
                    ref={setOverlayHost}
                    data-slot="drawer-overlay-host"
                    className="absolute start-0 top-0 size-0"
                />
            </DrawerPrimitive.Content>
        </DrawerPortal>
    );
}

function DrawerHeader({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="drawer-header"
            className={cn(
                'flex flex-col gap-0.5 p-4 group-data-[vaul-drawer-direction=bottom]/drawer-content:text-center group-data-[vaul-drawer-direction=top]/drawer-content:text-center md:gap-0.5 md:text-left',
                className,
            )}
            {...props}
        />
    );
}

function DrawerFooter({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="drawer-footer"
            className={cn(
                'mt-auto flex flex-col gap-2 border-t border-border-default p-4',
                className,
            )}
            {...props}
        />
    );
}

function DrawerTitle({ className, ...props }: React.ComponentProps<typeof DrawerPrimitive.Title>) {
    return (
        <DrawerPrimitive.Title
            data-slot="drawer-title"
            className={cn('text-base font-semibold text-text-heading', className)}
            {...props}
        />
    );
}

function DrawerDescription({
    className,
    ...props
}: React.ComponentProps<typeof DrawerPrimitive.Description>) {
    return (
        <DrawerPrimitive.Description
            data-slot="drawer-description"
            className={cn('text-sm text-text-secondary', className)}
            {...props}
        />
    );
}

export {
    Drawer,
    DrawerPortal,
    DrawerOverlay,
    DrawerTrigger,
    DrawerClose,
    DrawerContent,
    DrawerHeader,
    DrawerFooter,
    DrawerTitle,
    DrawerDescription,
};
